import React from 'react';
import { Toast } from '../../toast';

interface ICommitteeSubscribeButtonProps {
    committeeId: number;
    isLoggedIn?: boolean;
    loginUrl?: string;
}

interface ICommitteeSubscribeButtonState {
    isSubscribed: boolean;
    isLoading: boolean;
    error: string | null;
}

export class CommitteeSubscribeButton extends React.Component<ICommitteeSubscribeButtonProps, ICommitteeSubscribeButtonState> {
    constructor(props: ICommitteeSubscribeButtonProps) {
        super(props);
        this.state = {
            isSubscribed: false,
            isLoading: true,
            error: null
        };
    }

    componentDidMount() {
        if (this.props.isLoggedIn === false) {
            this.setState({ isLoading: false });
            return;
        }

        var pendingCommitteeId = this.getQueryParam('subscribeCommittee');
        if (pendingCommitteeId && parseInt(pendingCommitteeId, 10) === this.props.committeeId) {
            this.removeQueryParam('subscribeCommittee');
            this.subscribe();
        } else {
            this.checkSubscriptionStatus();
        }
    }

    componentDidUpdate(prevProps: ICommitteeSubscribeButtonProps) {
        // Check status again if committeeId changes
        if (prevProps.committeeId !== this.props.committeeId) {
            this.checkSubscriptionStatus();
        }
    }

    private getQueryParam(name: string): string | null {
        if (typeof window === 'undefined' || !window.location) return null;
        var match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
        return match ? decodeURIComponent(match[1]) : null;
    }

    private removeQueryParam(name: string): void {
        var search = window.location.search;
        var regex = new RegExp('[?&]' + name + '=[^&]*');
        var newSearch = search.replace(regex, '');
        if (newSearch.indexOf('?') !== 0 && newSearch.indexOf('&') === 0) {
            newSearch = '?' + newSearch.substring(1);
        }
        if (newSearch === '?') {
            newSearch = '';
        }
        var newUrl = window.location.pathname + newSearch + window.location.hash;
        window.history.replaceState(null, '', newUrl);
    }

    private checkSubscriptionStatus() {
        const { committeeId } = this.props;

        this.setState({ isLoading: true });

        $.ajax({
            url: '/api/SubscribedCommittees/IsCommitteeSubscribed',
            type: 'POST',
            data: JSON.stringify({ committeeId }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            success: (data) => {
                this.setState({
                    isSubscribed: data.isSubscribed,
                    isLoading: false,
                    error: null
                });
            },
            error: (xhr) => {
                if (xhr.status === 401) {
                    this.redirectToLogin();
                } else {
                    this.setState({
                        isLoading: false,
                        error: xhr.responseJSON?.message || 'Failed to check subscription status'
                    });
                }
            }
        });
    }

    private notifySubscriptionChanged(committeeId: number, isSubscribed: boolean) {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('committeeSubscriptionChanged', {
                detail: { committeeId, isSubscribed }
            }));
        }
    }

    private subscribe() {
        const { committeeId } = this.props;

        this.setState({ isLoading: true });

        $.ajax({
            url: '/api/SubscribedCommittees/Subscribe',
            type: 'POST',
            data: JSON.stringify({ committeeId }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            success: () => {
                this.setState({
                    isSubscribed: true,
                    isLoading: false
                });
                this.notifySubscriptionChanged(committeeId, true);
            },
            error: (xhr) => {
                if (xhr.status === 401) {
                    this.redirectToLogin();
                } else {
                    this.setState({
                        isLoading: false,
                        error: xhr.responseJSON?.message || 'Failed to subscribe'
                    });
                }
            }
        });
    }

    private redirectToLogin() {
        Toast.SetPendingToast(
            "Please login to access 'My MISO Calendar' to add/remove meetings and committee subscriptions. If you do not have a MISO Public Website account, click on 'Create Profile' below to create an account.",
            'My MISO Calendar',
            'info'
        );
        var loginUrl = this.props.loginUrl || '/login';
        var separator = window.location.search ? '&' : '?';
        var returnUrl = encodeURIComponent(window.location.pathname + window.location.search + separator + 'subscribeCommittee=' + this.props.committeeId + window.location.hash);
        window.location.href = loginUrl + '?returnUrl=' + returnUrl;
    }

    private handleToggleSubscription = () => {
        if (this.props.isLoggedIn === false) {
            this.redirectToLogin();
            return;
        }

        const { committeeId } = this.props;
        const { isSubscribed } = this.state;

        this.setState({ isLoading: true });

        const url = isSubscribed
            ? '/api/SubscribedCommittees/Unsubscribe'
            : '/api/SubscribedCommittees/Subscribe';

        $.ajax({
            url,
            type: 'POST',
            data: JSON.stringify({ committeeId }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            success: () => {
                this.setState({
                    isSubscribed: !isSubscribed,
                    isLoading: false
                });
                this.notifySubscriptionChanged(committeeId, !isSubscribed);
            },
            error: (xhr) => {
                this.setState({
                    isLoading: false,
                    error: xhr.responseJSON?.message || 'Failed to update subscription'
                });
            }
        });
    }

    render() {
        const { isSubscribed, isLoading, error } = this.state;

        if (isLoading) {
            return (
                <button className="btn btn-link dark-blue padding-0" disabled>
                    <i className="fa fa-spinner fa-spin margin-right-10" aria-hidden="true"></i>
                    Loading...
                </button>
            );
        }

        return (
            <button
                className="btn btn-link dark-blue padding-0"
                onClick={this.handleToggleSubscription}
                title={isSubscribed
                    ? "Remove all committee meetings from calendar"
                    : "Add all this committee's meetings to My MISO Calendar"}
            >
                <i className={`fa ${isSubscribed ? 'fa-calendar-times-o' : 'fa-calendar-plus-o'} margin-right-10`} aria-hidden="true"></i>
                {isSubscribed ? 'Remove all committee meetings from calendar' : 'Add all committee meetings to calendar'}
            </button>
        );
    }
}

export default CommitteeSubscribeButton;